/**
 * The typed client.
 *
 * `hc<AppType>` imports the Hono server's route types directly, so the client's request and
 * response shapes come from the server's actual handlers rather than from a hand-maintained
 * description of them. Rename a field in a route and every caller fails to compile.
 *
 * This is the concrete reason the frontends and the backend share a repository (D1): across a
 * repository boundary the same guarantee would need a published types package on every route
 * change, and the version that was published would be the one that drifted.
 *
 * Kept platform-agnostic, because `apps/mobile` uses it unchanged in Phase 7.
 */

import { hc } from "hono/client";
import type { AppType } from "@powwow/api";
import type { ApiError, ErrorCode } from "@powwow/validation";

export interface ClientOptions {
  baseUrl: string;
  /**
   * Which community the caller is acting in. Sent as a header rather than a path segment, because
   * `app.pow-wow.ca` serves every community from one hostname and the server re-checks membership
   * on every request — the header names a community, it does not grant access to one.
   */
  communityId?: string | undefined;
  /** Injected so a test can supply one and React Native can supply its own. */
  fetch?: typeof globalThis.fetch;
}

/** Reads a cookie by name. Returns undefined where there is no document — Workers, native. */
function readCookie(name: string): string | undefined {
  const jar = (globalThis as { document?: { cookie?: string } }).document?.cookie;
  if (!jar) return undefined;
  for (const part of jar.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === name) return decodeURIComponent(rest.join("="));
  }
  return undefined;
}

/**
 * Which deployment this page belongs to, read off its own hostname: `""`, `"_dev"`, `"_uat"`.
 *
 * Three deployments share `pow-wow.ca`, and the cookies they issue are scoped to `.pow-wow.ca`
 * because `app-uat.` has to call `api-uat.` — so the *name* is the only thing distinguishing one
 * deployment's session from another's, and a client reading the wrong one echoes nothing and has
 * every write refused as a CSRF failure. `apps/api/src/hosts.ts` derives the same name from the
 * `HOST_SUFFIX` var on the server side; `index.test.ts` asserts the two agree.
 *
 * Derived at runtime rather than injected at build time, for the same reason `api-base.ts` derives
 * the API origin that way: a bundle built without its env var still has to be right, and the host
 * is the one thing a deployed page can always trust. On a native client there is no location and
 * no cookie jar — the suffix is empty and unused, because the session travels as a bearer token.
 */
export function deploymentSuffix(hostname?: string): string {
  const host =
    hostname ?? (globalThis as { location?: { hostname?: string } }).location?.hostname ?? "";
  const label = host.split(".")[0] ?? "";
  return /-(?:dev|uat)$/.exec(label)?.[0].replace("-", "_") ?? "";
}

export const CSRF_COOKIE = `pw_csrf${deploymentSuffix()}`;
export const CSRF_HEADER = "x-powwow-csrf";
export const COMMUNITY_HEADER = "x-powwow-community";

export function createClient(options: ClientOptions) {
  const baseFetch = options.fetch ?? globalThis.fetch;

  return hc<AppType>(options.baseUrl, {
    fetch: (input: RequestInfo | URL, init?: RequestInit) => {
      const headers = new Headers(init?.headers);

      if (options.communityId) headers.set(COMMUNITY_HEADER, options.communityId);

      // Double-submit CSRF: echo the readable cookie into a header the server compares. Only
      // needed on unsafe methods, but setting it unconditionally is simpler and harmless.
      const csrf = readCookie(CSRF_COOKIE);
      if (csrf) headers.set(CSRF_HEADER, csrf);

      // `credentials` is a browser concept and is absent from the Workers `RequestInit` type,
      // which is what this package compiles against. It is set anyway and cast, because the
      // browser is where this client actually runs and the session cookie lives on a sibling
      // subdomain — without it, every authenticated request from `app.` to `api.` is anonymous.
      // Runtimes that do not know the field ignore it.
      const withCredentials = { ...init, headers, credentials: "include" } as RequestInit;

      return withNetworkErrors(() => baseFetch(input, withCredentials));
    },
  });
}

export type ApiClient = ReturnType<typeof createClient>;

/**
 * A failed response, as an error worth throwing.
 *
 * Carries the field errors through, because the interface has to place each one next to the
 * control it belongs to rather than as a summary at the top of the form (ux.md §3.2).
 */
export class ApiRequestError extends Error {
  readonly code: ErrorCode;
  readonly status: number;
  readonly fields: ReadonlyArray<{ path: string; message: string }>;
  readonly requestId: string | undefined;

  constructor(status: number, payload: ApiError, cause?: unknown) {
    super(payload.error.message, cause !== undefined ? { cause } : undefined);
    this.name = "ApiRequestError";
    this.status = status;
    this.code = payload.error.code;
    this.fields = payload.error.fields ?? [];
    this.requestId = payload.error.requestId;
  }

  /** Errors on one control, for rendering inline. */
  fieldErrors(path: string): string[] {
    return this.fields.filter((field) => field.path === path).map((field) => field.message);
  }
}

/**
 * Run a `fetch` so that a request which never reached the server fails like every other request.
 *
 * `fetch` rejects with a bare `TypeError` when the network is the problem, and the browsers do not
 * even agree on its wording: "Failed to fetch" in Chrome, "Load failed" in Safari, "NetworkError
 * when attempting to fetch resource" in Firefox. Those strings reach the screen verbatim anywhere a
 * caller renders `error.message` — and every caller does, because for a *response* that is exactly
 * the right thing to render.
 *
 * At an arena on one bar of signal this is the likeliest failure of the lot, and the one a
 * volunteer can least act on: "Load failed" names no cause and suggests no next step, so it reads
 * as "the app is broken" rather than "you have no signal". It is also the failure most likely to
 * be transient, which is the one fact worth putting on screen.
 *
 * So it is converted here, at the one place a `fetch` rejection can be caught, into the same
 * `ApiRequestError` envelope every other failure arrives in — one error type for callers, and
 * wording we chose rather than wording the browser chose. The original is kept as `cause` for the
 * console; nothing is swallowed.
 */
export async function withNetworkErrors(send: () => Promise<Response>): Promise<Response> {
  try {
    return await send();
  } catch (cause) {
    // An `ApiRequestError` from an inner layer is already the words we chose — pass it through
    // rather than relabelling a real server answer as a connection problem.
    if (cause instanceof ApiRequestError) throw cause;
    throw new ApiRequestError(
      0,
      {
        error: {
          code: "unavailable",
          message: "Couldn't reach the server. Check your connection and try again.",
        },
      },
      cause,
    );
  }
}

/**
 * Unwrap a response, turning the error envelope into a thrown `ApiRequestError`.
 *
 * A non-JSON failure — a proxy error page, a network interruption mid-response — becomes an
 * `unavailable` error with the reassurance the interface is required to give: the work is saved
 * locally and will be sent again.
 */
export async function unwrap<T>(response: Response): Promise<T> {
  if (response.ok) return (await response.json()) as T;

  let payload: ApiError;
  try {
    payload = (await response.json()) as ApiError;
  } catch {
    payload = {
      error: {
        code: response.status >= 500 ? "unavailable" : "internal",
        message: "That isn't working right now. Your work is saved.",
      },
    };
  }
  throw new ApiRequestError(response.status, payload);
}
